import React from "react";

// Known-framework registry with curated bilingual labels + country flag.
// Admins can add new frameworks in the CSV without a code change — see below.
export const FRAMEWORKS = {
  pdpl:      { flag: "🇸🇦", ar: "PDPL",     en: "PDPL" },
  gdpr:      { flag: "🇪🇺", ar: "GDPR",     en: "GDPR" },
  uae_pdpl:  { flag: "🇦🇪", ar: "UAE PDPL", en: "UAE PDPL" },
  qatar_pdp: { flag: "🇶🇦", ar: "Qatar PDP", en: "Qatar PDP" },
};

// Slug-safe id for the data-testid attribute of arbitrary admin-entered labels.
function slugForTestId(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "custom";
}

// Split a leading emoji flag (or any leading emoji) from a free-form label.
// Accepts values like:
//   "🇸🇾 Syrian Data Protection"
//   "🇴🇲 Oman Data Protection framework"
//   "SomeFramework"                             (no flag)
// Returns { flag, label } — flag may be "".
function splitFlagAndLabel(raw) {
  const s = String(raw).trim();
  // Regional-indicator flag = two chars in U+1F1E6-U+1F1FF
  const flagMatch = s.match(/^([\uD83C][\uDDE6-\uDDFF][\uD83C][\uDDE6-\uDDFF])\s*(.*)$/);
  if (flagMatch) return { flag: flagMatch[1], label: flagMatch[2].trim() };
  // Any other leading emoji (fallback, keeps things forgiving)
  const genericMatch = s.match(/^(\p{Extended_Pictographic}\uFE0F?)\s*(.*)$/u);
  if (genericMatch) return { flag: genericMatch[1], label: genericMatch[2].trim() };
  return { flag: "", label: s };
}

/**
 * Parse a CSV into an array of badge descriptors.
 * - Known keys (case-insensitive) resolve to the curated FRAMEWORKS entry.
 * - Unknown values are rendered as-is, splitting an optional leading flag emoji
 *   from a free-text label so admins can add "🇸🇾 Syrian Data Protection" without
 *   a code change.
 */
export function parseFrameworks(csv) {
  if (!csv) return [];
  const items = Array.isArray(csv) ? csv : String(csv).split(",");
  return items
    .map((raw) => String(raw).trim())
    .filter(Boolean)
    .map((raw) => {
      const known = FRAMEWORKS[raw.toLowerCase()];
      if (known) {
        return { id: raw.toLowerCase(), flag: known.flag, ar: known.ar, en: known.en };
      }
      const { flag, label } = splitFlagAndLabel(raw);
      return { id: slugForTestId(label || raw), flag, ar: label, en: label };
    });
}

/** Non-committal "Based on / وفقاً لـ" badge — never says "Compliant". */
export default function RegulatoryBadges({ value, lang = "en", size = "sm", testId }) {
  const items = parseFrameworks(value);
  if (!items.length) return null;
  const prefix = lang === "ar" ? "وفقاً لـ" : "Based on";
  const pad = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]";
  return (
    <div className="inline-flex flex-wrap gap-1.5" data-testid={testId || "regulatory-badges"}>
      {items.map((f) => (
        <span key={f.id}
              className={`inline-flex items-center gap-1 rounded-sm border font-semibold uppercase tracking-widest ${pad}`}
              style={{ color: "#8C6A2E", borderColor: "#C9A961", background: "rgba(201,169,97,0.08)" }}
              data-testid={`reg-badge-${f.id}`}>
          <span>{prefix}</span>
          {f.flag ? <span aria-hidden>{f.flag}</span> : null}
          <span>{lang === "ar" ? f.ar : f.en}</span>
        </span>
      ))}
    </div>
  );
}
